import React, { useState, useCallback, useMemo } from 'react';
import { isBefore, subHours } from 'date-fns';
import { CircularProgress, Button, styled } from '@mui/material';
import useKyc from 'hooks/data/kyc/useKyc';
import downloadKycPdf from 'helpers/investor/downloadKycPdf';
import { AccountType } from 'further-types/investor';
import KycResultsButton from './KycResultsButton';
import CertificateItem from './CertificateItem';
import {
  Api,
  KycProcessStatus,
  AmlResultsArchiveStatus,
} from 'further-types/kyc';
import KycManualApprovalModal from './KycManualApprovalModal';
import { CalendarTime } from 'further-ui/utils';
import { useDisclosure } from 'further-ui/hooks';
import AlertDialog from 'components/AlertDialog';
import { kycRiskRatingLabels, KycStatusLabel } from 'further-ui/labels';
import KycStatus from './KycStatus';
import PreviousResults from './PreviousResults';
import { Attachment } from '@mui/icons-material';
import Spacing from 'components/Spacing';
import RecordRow from 'components/RecordRow';
import PageHeader from 'components/PageHeader';
import Heading from 'components/Heading';
import ResponsiveActions from 'components/PageHeader/ResponsiveActions';
import { Column, Row } from 'components/Layout';
import AmlResults from './AmlResults';
import AmlResultsTable from './AmlResultsTable';
import { useGetRole } from 'hooks/ui/useGetRole';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';
import {
  canResetKyc,
  canUnapproveAml,
  canApproveIdv,
  canApproveAml,
} from 'further-ui/utils';
import api from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';

const AttachmentIcon = styled(Attachment)({
  fontSize: '18px',
});

const AttachmentLink = styled('a')(({ theme }) => ({
  fontSize: '12px',
  color: theme.palette.text.primary,

  '&:hover': {
    color: theme.palette.text.rootColor,
  },
}));

type Props = {
  investorId: string;
  fullName?: string;
  accountType?: AccountType;
  identityVerificationCertificates?: Array<{
    adviserId: string;
    adviser?: {
      _id: string;
      fullName: string;
    };
    investmentId?: string;
    investment?: {
      _id: string;
      investmentDate?: CalendarTime;
      externalInvestmentId?: string;
      fundId?: { fundName?: string };
    };
    date: CalendarTime;
    url: string;
  }>;
};

const KycResults: React.FC<Props> = ({
  investorId,
  fullName,
  identityVerificationCertificates,
  accountType,
}) => {
  const manualApprovalModal = useDisclosure();
  const resetKycConfirmationModal = useDisclosure();
  const runAmlCheckModal = useDisclosure();
  const [showAmlResults, setShowAmlResults] = useState(false);
  const [amlResultsStatusFilter, setAmlResultsStatusFilter] =
    useState<AmlResultsArchiveStatus>(AmlResultsArchiveStatus.NotArchived);
  const { isSuperAdmin } = useGetRole();
  const [canEdit] = useHasPermissions([RolePermission.EditInvestorKyc]);

  const runAmlCheckMutation = api.kyc.runAmlCheck.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'AML check started.',
      invalidations: [['investor-kyc', investorId]],
      onSuccess: () => {
        runAmlCheckModal.onClose();
      },
    }),
  );

  const {
    kycDetails: { data: kycData, isFetching, isLoading: isKycLoading },
    expiredKyc: { data: expiredKycData, isFetching: isFetchingExpiredData },
    getKycImages,
    resetKyc,
    approveIdv,
    approveAml,
    manualKycProcess,
    saveAmlApprovalNotes,
    saveIdvApprovalNotes,
    archiveAmlHit,
    unarchiveAmlHit,
    markAmlResultsAsViewed,
    unapproveAml,
  } = useKyc(investorId);

  const { mutate: refetchFromYoti, isPending: isRefetchingFromYoti } =
    api.kyc.refetchFromYoti.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'KYC data re-fetched successfully.',
        invalidations: [['investor-kyc', investorId]],
      }),
    );

  const handleDownloadKycResults = async (kycDetails: typeof kycData) => {
    const images = await getKycImages();
    //@ts-expect-error
    downloadKycPdf(kycDetails, images.data ?? {}, { fullName });
  };

  // Memoize filtered results to prevent unnecessary recalculations
  const filteredAmlResults = useMemo(() => {
    return (
      kycData?.amlPotentialMatches?.filter((match) => {
        if (amlResultsStatusFilter === AmlResultsArchiveStatus.NotArchived) {
          return !match.archived;
        } else if (
          amlResultsStatusFilter === AmlResultsArchiveStatus.Archived
        ) {
          return match.archived;
        }
        return true;
      }) ?? []
    );
  }, [kycData?.amlPotentialMatches, amlResultsStatusFilter]);

  const handleArchiveAmlHit = useCallback(
    (caDocId: string) => {
      archiveAmlHit({ caDocId });
    },
    [archiveAmlHit],
  );

  const handleUnarchiveAmlHit = useCallback(
    (caDocId: string) => {
      unarchiveAmlHit({ caDocId });
    },
    [unarchiveAmlHit],
  );

  const handleAmlResultsStatusFilterChange = (
    value: AmlResultsArchiveStatus,
  ) => {
    setAmlResultsStatusFilter(value);
  };

  const toggleAmlResults = useCallback(() => {
    setShowAmlResults(!showAmlResults);
    if (!showAmlResults && !isSuperAdmin) {
      markAmlResultsAsViewed({});
    }
  }, [showAmlResults, isSuperAdmin, markAmlResultsAsViewed]);

  const handleDownloadResults = useCallback(() => {
    handleDownloadKycResults(kycData);
  }, [kycData, handleDownloadKycResults]);

  if ((isFetching || isFetchingExpiredData) && (!kycData || !expiredKycData)) {
    return <CircularProgress size={24} />;
  }

  const isLegalEntity = accountType === AccountType.LegalEntity;

  const showApproveIdvButton =
    canEdit && canApproveIdv(kycData) && !isLegalEntity;
  const showApproveAmlButton =
    canEdit && canApproveAml(kycData) && !isLegalEntity;

  const showResetKycButton =
    canEdit && canResetKyc(kycData ?? undefined) && !isLegalEntity;

  const showUnapproveAmlButton =
    canEdit && canUnapproveAml(kycData) && !isLegalEntity;

  const isPassedManually =
    kycData?.amlProcessStatus === KycProcessStatus.ManuallyApproved ||
    kycData?.idvProcessStatus === KycProcessStatus.ManuallyApproved;

  const showDownloadResultsButton = !!(
    kycData?.isKycStepCompleted && kycData?.hasYotiData
  );

  const isKycSessionOverOneHourOld =
    !!kycData?.sessionCreationTime &&
    isBefore(new Date(kycData.sessionCreationTime), subHours(new Date(), 1));

  const showRefetchFromYotiButton =
    canEdit &&
    kycData?.isKycDataProcessing &&
    isKycSessionOverOneHourOld &&
    !isLegalEntity &&
    !kycData?.expiredAt &&
    !!kycData?.sessionId &&
    kycData?.yotiStatus === 'ONGOING';

  const handleResetKyc = () => {
    resetKyc();
    resetKycConfirmationModal.onClose();
  };

  const handleManualKycProcess = (data: Api.ManualKycProcessRequest) => {
    manualKycProcess(data);
    manualApprovalModal.onClose();
  };

  const handleDownloadNotes = () => {
    const blob = new Blob(
      [kycData?.manualApprovalNotes?.replace(/<[^>]*>/g, '') ?? ''],
      {
        type: 'text/plain',
      },
    );

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'notes.txt';
    document.body.appendChild(a);
    a.click();
    URL.revokeObjectURL(url);
    a.remove();
  };

  const handleApproveIdv = (idvApprovalNotes: string) => {
    approveIdv({ idvApprovalNotes });
  };

  const handleApproveAml = (amlApprovalNotes: string) => {
    approveAml({ amlApprovalNotes });
  };

  const handleUnapproveAml = () => {
    unapproveAml({});
  };

  const handleSaveIdvApprovalNotes = (idvApprovalNotes: string) => {
    saveIdvApprovalNotes({ idvApprovalNotes });
  };

  const handleSaveAmlApprovalNotes = (amlApprovalNotes: string) => {
    saveAmlApprovalNotes({ amlApprovalNotes });
  };

  const handleConfirmRunAmlCheck = () => {
    runAmlCheckMutation.mutate({ investorId });
  };

  const hasAmlResults =
    !!kycData?.amlProcessStatus &&
    kycData?.amlProcessStatus !== KycProcessStatus.NotAttempted &&
    kycData?.amlProcessStatus !== KycProcessStatus.Reset;

  const hasLiveAml =
    !!kycData?.amlProcessStatus &&
    [
      KycProcessStatus.Passed,
      KycProcessStatus.AdminApproved,
      KycProcessStatus.ManuallyApproved,
      KycProcessStatus.InProgress,
      KycProcessStatus.Processing,
    ].includes(kycData.amlProcessStatus);

  const showRunAmlCheckButton = canEdit && !hasLiveAml;

  const sectionTitle = isLegalEntity ? 'KYB information' : 'KYC results';
  const manualRecordTitle = isLegalEntity
    ? 'Manual KYB record'
    : 'Manual KYC record';
  const manualApprovalButtonTitle = isLegalEntity
    ? isPassedManually
      ? 'Edit manual KYB'
      : 'Complete manual KYB'
    : isPassedManually
    ? 'Edit manual KYC'
    : 'Upload manual KYC';

  return (
    <Spacing>
      <PageHeader>
        <Heading variant="h2" noMargin>
          {sectionTitle}
        </Heading>
        <ResponsiveActions>
          {showResetKycButton && (
            <Button
              onClick={resetKycConfirmationModal.onOpen}
              variant="contained"
              color="primary"
              size="small"
            >
              Reset eKYC
            </Button>
          )}
          {canEdit && (
            <Button
              onClick={manualApprovalModal.onOpen}
              variant="contained"
              color="primary"
              size="small"
            >
              {manualApprovalButtonTitle}
            </Button>
          )}
        </ResponsiveActions>
      </PageHeader>

      <Spacing size="sm">
        {isLegalEntity ? (
          <RecordRow label="KYB status">
            <Column spacing="xs" fullWidth>
              <KycStatus
                approveButtonText="Admin approve IDV"
                showApproveButton={false}
                statusLabel={
                  kycData?.isKycProcessPassed ? 'Approved' : 'Not approved'
                }
                showDownloadResultsButton={false}
                showNotesTooltip={false}
                showRefetchFromYotiButton={false}
              />
              <PreviousResults
                expiredKycData={expiredKycData}
                type="idv"
                onDownloadResults={handleDownloadResults}
              />
            </Column>
          </RecordRow>
        ) : (
          <>
            <RecordRow label="IDV status">
              <Column spacing="xs" fullWidth>
                <KycStatus
                  showApproveButton={showApproveIdvButton}
                  //@ts-expect-error
                  handleApprove={handleApproveIdv}
                  approveButtonText="Approve IDV"
                  statusLabel={
                    kycData?.idvStatusLabel ?? KycStatusLabel.NotAttempted
                  }
                  showDownloadResultsButton={showDownloadResultsButton}
                  showRefetchFromYotiButton={!!showRefetchFromYotiButton}
                  refetchFromYoti={() => refetchFromYoti({ investorId })}
                  isRefetchingFromYoti={isRefetchingFromYoti}
                  handleDownloadResults={handleDownloadResults}
                  approvalNotes={kycData?.idvApprovalNotes ?? ''}
                  showNotesTooltip={
                    kycData?.idvProcessStatus === KycProcessStatus.AdminApproved
                  }
                  //@ts-expect-error
                  handleSaveNotes={handleSaveIdvApprovalNotes}
                />
                <PreviousResults
                  expiredKycData={expiredKycData}
                  type="idv"
                  onDownloadResults={handleDownloadResults}
                />
              </Column>
            </RecordRow>

            <RecordRow label="AML status">
              <Column spacing="xs" fullWidth>
                <KycStatus
                  approveButtonText="Approve AML"
                  unapproveButtonText="Unapprove AML"
                  showApproveButton={showApproveAmlButton}
                  showUnapproveButton={showUnapproveAmlButton}
                  handleUnapprove={handleUnapproveAml}
                  //@ts-expect-error
                  handleApprove={handleApproveAml}
                  statusLabel={
                    kycData?.amlStatusLabel ?? KycStatusLabel.NotAttempted
                  }
                  approvalNotes={kycData?.amlApprovalNotes ?? ''}
                  showNotesTooltip={
                    kycData?.amlProcessStatus === KycProcessStatus.AdminApproved
                  }
                  //@ts-expect-error
                  handleSaveNotes={handleSaveAmlApprovalNotes}
                />
              </Column>
            </RecordRow>

            <RecordRow label="Adviser submitted documents">
              {identityVerificationCertificates?.length
                ? identityVerificationCertificates?.map((certificate, idx) => (
                    <CertificateItem certificate={certificate} key={idx} />
                  ))
                : '-'}
            </RecordRow>
          </>
        )}
        {hasAmlResults ? (
          <RecordRow label="AML results">
            <Column spacing="xs" fullWidth>
              <AmlResults
                potentialUnarchivedMatches={
                  kycData?.amlPotentialMatches?.filter(
                    (match) => !match.archived,
                  ).length ?? 0
                }
                potentialMatches={kycData?.amlPotentialMatches?.length ?? 0}
                potentialArchivedMatches={
                  kycData?.amlPotentialMatches?.filter(
                    (match) => match.archived,
                  ).length ?? 0
                }
                showAmlResults={showAmlResults}
                toggleAmlResults={toggleAmlResults}
                amlHasUpdates={kycData?.amlHasUpdates ?? false}
                handleAmlResultsStatusFilterChange={
                  handleAmlResultsStatusFilterChange
                }
                amlResultsStatusFilter={amlResultsStatusFilter}
                runAmlCheckButton={
                  showRunAmlCheckButton ? (
                    <Button
                      size="extra-small"
                      variant="outlined"
                      color="primary"
                      onClick={runAmlCheckModal.onOpen}
                      disabled={runAmlCheckMutation.isPending}
                    >
                      Run AML check
                    </Button>
                  ) : undefined
                }
              />
            </Column>
          </RecordRow>
        ) : showRunAmlCheckButton ? (
          <RecordRow label="AML results">
            <Column spacing="xs" fullWidth>
              <Row centered spacing="sm">
                <span>No AML check</span>
                <Button
                  size="extra-small"
                  variant="outlined"
                  color="primary"
                  onClick={runAmlCheckModal.onOpen}
                  disabled={runAmlCheckMutation.isPending}
                >
                  Run AML check
                </Button>
              </Row>
            </Column>
          </RecordRow>
        ) : null}
        {showAmlResults && (
          <AmlResultsTable
            results={filteredAmlResults}
            handleArchiveAmlHit={handleArchiveAmlHit}
            handleUnarchiveAmlHit={handleUnarchiveAmlHit}
            investorId={investorId}
            isLoading={isKycLoading}
          />
        )}
        <RecordRow label="Risk rating">
          {kycRiskRatingLabels.find(
            (label) => label.value === kycData?.riskRating,
          )?.label ?? kycRiskRatingLabels[0].label}
        </RecordRow>
        {kycData?.manualApprovalFiles?.length ||
        kycData?.manualApprovalNotes?.length ? (
          <RecordRow label={manualRecordTitle}>
            <Column spacing="sm">
              <div>
                <KycResultsButton
                  visible={!!kycData?.manualApprovalNotes?.length}
                  onClick={handleDownloadNotes}
                  buttonText="Download notes"
                />
              </div>
              <Column spacing="xs">
                {kycData?.manualApprovalFiles?.map(({ name, url }) => (
                  <Row centered spacing="xs" key={name}>
                    <AttachmentIcon />
                    <AttachmentLink target="_blank" rel="noreferrer" href={url}>
                      {name}
                    </AttachmentLink>
                  </Row>
                ))}
              </Column>
            </Column>
          </RecordRow>
        ) : null}
      </Spacing>

      {manualApprovalModal.isOpen && (
        <KycManualApprovalModal
          onClose={manualApprovalModal.onClose}
          processManualKyc={handleManualKycProcess}
          isPassedManually={isPassedManually}
          //@ts-expect-error
          kycData={kycData}
          isLegalEntity={isLegalEntity}
        />
      )}
      {resetKycConfirmationModal.isOpen && (
        <AlertDialog
          open
          title="Warning"
          content="Are you sure you wish to reset this investor's eKYC? This cannot be undone."
          btnLabels={{
            cancel: 'Go back',
            confirm: 'Reset eKYC',
          }}
          onClose={resetKycConfirmationModal.onClose}
          onConfirm={handleResetKyc}
        />
      )}
      {runAmlCheckModal.isOpen && (
        <AlertDialog
          open
          title="Running new AML check"
          content="Continuing will incur a one-off and ongoing charge for daily AML checking for this investor. Are you sure you wish to continue?"
          btnLabels={{
            cancel: 'Go back',
            confirm: 'Run check',
          }}
          onClose={runAmlCheckModal.onClose}
          onConfirm={handleConfirmRunAmlCheck}
          confirmBtnProps={{
            disabled: runAmlCheckMutation.isPending,
            loading: runAmlCheckMutation.isPending,
          }}
        />
      )}
    </Spacing>
  );
};

export default KycResults;
